import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemMetricsHeader = ({ onEmergencyAction }) => {
  const [metrics, setMetrics] = useState({
    activeAuctions: 247,
    totalBuyers: 89,
    avgResponseTime: 145,
    systemLoad: 67,
    revenue: 12847.50,
    conversionRate: 23.4,
    errorRate: 0.2,
    uptime: 99.9
  });

  const [emergencyMode, setEmergencyMode] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    // Simulate real-time metrics updates
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        activeAuctions: prev?.activeAuctions + Math.floor(Math.random() * 10 - 5),
        avgResponseTime: Math.max(50, prev?.avgResponseTime + Math.floor(Math.random() * 20 - 10)),
        systemLoad: Math.max(0, Math.min(100, prev?.systemLoad + Math.floor(Math.random() * 10 - 5))),
        revenue: prev?.revenue + Math.random() * 50,
        errorRate: Math.max(0, prev?.errorRate + (Math.random() - 0.5) * 0.1)
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Generate alerts based on metrics
    const newAlerts = [];
    
    if (metrics?.systemLoad > 80) {
      newAlerts?.push({
        id: 'high-load',
        type: 'warning',
        message: 'High system load detected'
      });
    }
    
    if (metrics?.avgResponseTime > 200) {
      newAlerts?.push({
        id: 'slow-response',
        type: 'error',
        message: 'Slow buyer response times'
      });
    }
    
    if (metrics?.errorRate > 1.0) {
      newAlerts?.push({
        id: 'high-errors',
        type: 'error',
        message: 'Elevated error rate'
      });
    }

    setAlerts(newAlerts);
  }, [metrics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getMetricColor = (value, thresholds) => {
    if (value >= thresholds?.danger) return 'text-error';
    if (value >= thresholds?.warning) return 'text-warning';
    return 'text-success';
  };

  const getLoadBarColor = (load) => {
    if (load >= 80) return 'bg-error';
    if (load >= 60) return 'bg-warning';
    return 'bg-success';
  };

  const handleEmergencyToggle = () => {
    setEmergencyMode(!emergencyMode);
    onEmergencyAction(emergencyMode ? 'disable' : 'enable');
  };

  return (
    <div className="bg-card border-b border-border">
      {/* Alert Bar */}
      {alerts?.length > 0 && (
        <div className="bg-warning/10 border-b border-warning/20 px-6 py-2">
          <div className="flex items-center space-x-4">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <div className="flex items-center space-x-6">
              {alerts?.map((alert) => (
                <span key={alert?.id} className="text-sm text-warning">
                  {alert?.message}
                </span>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => setAlerts([])}
              className="ml-auto"
            />
          </div>
        </div>
      )}
      {/* Main Metrics */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left: Core Metrics */}
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={20} className="text-accent" />
              <div>
                <h1 className="text-lg font-semibold text-foreground">Bidding Engine</h1>
                <p className="text-xs text-muted-foreground">Real-time monitoring</p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{metrics?.activeAuctions}</div>
                <div className="text-xs text-muted-foreground">Active Auctions</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{metrics?.totalBuyers}</div>
                <div className="text-xs text-muted-foreground">Online Buyers</div>
              </div>
              
              <div className="text-center">
                <div className={`text-2xl font-bold ${getMetricColor(metrics?.avgResponseTime, { warning: 150, danger: 200 })}`}>
                  {metrics?.avgResponseTime}ms
                </div>
                <div className="text-xs text-muted-foreground">Avg Response</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-success">{formatCurrency(metrics?.revenue)}</div>
                <div className="text-xs text-muted-foreground">Today's Revenue</div>
              </div>
            </div>
          </div>

          {/* Right: System Status & Controls */}
          <div className="flex items-center space-x-4">
            {/* System Load */}
            <div className="flex items-center space-x-2">
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">{metrics?.systemLoad}%</div>
                <div className="text-xs text-muted-foreground">System Load</div>
              </div>
              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${getLoadBarColor(metrics?.systemLoad)}`}
                  style={{ width: `${metrics?.systemLoad}%` }}
                />
              </div>
            </div>

            {/* Performance Indicators */}
            <div className="flex items-center space-x-3 px-3 py-2 bg-muted/20 rounded-lg">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full pulse-live"></div>
                <span className="text-xs text-muted-foreground">
                  {metrics?.conversionRate}% CVR
                </span>
              </div>
              <div className="w-px h-4 bg-border"></div>
              <div className="flex items-center space-x-1">
                <Icon name="Zap" size={12} className="text-warning" />
                <span className="text-xs text-muted-foreground">
                  {metrics?.errorRate?.toFixed(1)}% Errors
                </span>
              </div>
            </div>

            {/* Emergency Controls */}
            <div className="flex items-center space-x-2">
              <Button
                variant={emergencyMode ? "destructive" : "outline"}
                size="sm"
                iconName={emergencyMode ? "AlertTriangle" : "Shield"}
                onClick={handleEmergencyToggle}
                className={emergencyMode ? "animate-pulse" : ""}
              >
                {emergencyMode ? "Emergency Mode" : "Emergency"}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                iconName="Pause"
                onClick={() => onEmergencyAction('pause_all')}
                title="Pause all auctions"
              />
              
              <Button
                variant="ghost"
                size="sm"
                iconName="Settings"
                title="System settings"
              />
            </div>
          </div>
        </div>

        {/* Secondary Metrics Bar */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Uptime:</span>
              <span className="text-foreground font-medium">{metrics?.uptime}%</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Database" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Queue:</span>
              <span className="text-foreground font-medium">12 pending</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Wifi" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">API Status:</span>
              <span className="text-success font-medium">Operational</span>
            </div>
          </div>

          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="RefreshCw" size={12} />
            <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMetricsHeader;